/**
 * The identifiers: what each names, why they name things rather than pages,
 * how one resolves to a page, and which never do. Written for the
 * researcher who will store them and the reader who will share one.
 */
import type { DocPage } from '../types.js';

export const identifiersPage: DocPage = {
  slug: 'identifiers',
  title: 'Identifiers and citation',
  audience: 'researchers',
  promise: 'What each identifier names, how it resolves to a page, which never do, and how to cite one.',
  lead: 'Everything in the graph has one identifier, minted from the record that names it and the same in every environment. An identifier names a thing, not a page; the site answers an identifier with the page about the thing, when there is one.',
  sections: [
    {
      id: 'scheme',
      heading: 'The scheme',
      blocks: [
        {
          kind: 'paragraph',
          text: ['Identifiers live under https://fundergraph.org/id/ and are built from the natural keys of the records, never from a counter or a random value, so the same record gets the same identifier on every build.'],
        },
        {
          kind: 'table',
          header: ['Identifier', 'Names'],
          rows: [
            [['…/id/org/{EIN}'], ['An organization, by its EIN as the IRS records it.']],
            [['…/id/filing/{object identifier}'], ['One e-filed return, by the number the IRS assigns to the document.']],
            [['…/id/grant/{filer EIN}/{tax period}/{hash}'], ['One grant row of one return, by the filer, the period, and a hash of the row.']],
            [['…/id/project/{sponsor EIN}/{slug}'], ['A sponsored project, scoped under its sponsor’s EIN and named by its name.']],
            [['…/id/subject/{slug}'], ['A subject in the classification.']],
            [['…/id/cluster/{recipe}/{window}/{hash}'], ['A computed map: the recipe, the window of years, and a hash of the anchor and parameters.']],
          ],
        },
        {
          kind: 'paragraph',
          text: [
            'An identifier is the same on the development site and the production site, because it names a thing and not a server. Whether it resolves is a matter of serving; the path /id/ is reserved for identifiers forever and no page of the site is routed under it.',
          ],
        },
      ],
    },
    {
      id: 'resolving',
      heading: 'How an identifier resolves',
      blocks: [
        {
          kind: 'paragraph',
          text: [
            'Opening an identifier in a browser answers with a redirect to the page about the thing it names: an organization’s identifier opens the organization’s page; a grant’s or a project’s identifier opens the page of the filer or the sponsor, where the grant or the project is shown in context; a subject’s identifier opens the subject page; a map’s identifier opens that map. An identifier that names something no page shows on its own, such as one return, answers “not found”, because a return is shown only on its filer’s page. That answer is not a broken link: the identifier is still the name of the thing, and the record it names is still in the graph.',
          ],
        },
      ],
    },
    {
      id: 'listing',
      heading: 'Which organizations have a page',
      blocks: [
        {
          kind: 'paragraph',
          text: [
            'Every organization in the master file has an identifier. Only an organization that files its own return, or that the master file records as having filed one, has a page; an identifier for any other organization answers with its name and number where a filing names it. ',
            { link: '/about/reading#listing', text: 'The listing rule' },
            ' is the reason, and ',
            { link: '/about/sponsors', text: 'the page for sponsors' },
            ' says how a project that has no page is shown.',
          ],
        },
      ],
    },
    {
      id: 'citing',
      heading: 'Citing an identifier',
      blocks: [
        {
          kind: 'paragraph',
          text: [
            'Cite an organization by its identifier or by its page, which carry the same EIN; cite a fact by the record behind it, as ',
            { link: '/about/verify#citing', text: 'the page on verifying' },
            ' shows. Identifiers are stable across builds, so one cited today names the same organization, return, or grant in any later build; what a later build may change is the facts shown about it, which is why a citation also carries the build date.',
          ],
        },
      ],
    },
  ],
};
